/*
  <stat-band> — optional count-up-on-scroll for the stat band section.

  Behavior:
  - Only runs when the section opts in (data-countup="true").
  - Skips entirely (renders final values) when the user prefers reduced motion.
  - Counts up INTEGER numeric values only. Non-integer / ranged / symbol-bearing
    values (e.g. "<50", "600–1250", "presetări") are left exactly as authored —
    we never fabricate or interpolate them.
  - Uses IntersectionObserver to trigger once on scroll into view, then
    unobserves. rAF drives the animation. Everything is keyed to this element's
    own cells, so multiple instances on a page never interfere.
*/
if (!customElements.get('stat-band')) {
  customElements.define(
    'stat-band',
    class StatBand extends HTMLElement {
      connectedCallback() {
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.optedIn = this.dataset.countup === 'true';
        this.cells = Array.from(this.querySelectorAll('.stat-band__cell'));

        if (!this.optedIn || this.reduced || !this.cells.length || !('IntersectionObserver' in window)) {
          // Static render: ensure cells are visible and numbers show final values.
          this.classList.remove('is-animated');
          return;
        }

        this.classList.add('is-animated');
        this.prepare();

        this.observer = new IntersectionObserver(
          (entries, obs) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                obs.unobserve(entry.target);
                this.run();
              }
            });
          },
          { threshold: 0.25, rootMargin: '0px 0px -8% 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      // Stash each cell's animatable integer target (if any) and zero it out.
      prepare() {
        this.targets = this.cells.map((cell) => {
          const numEl = cell.querySelector('.stat-band__num');
          if (!numEl) return null;
          const valueNode = this.firstTextNode(numEl);
          if (!valueNode) return null;
          const raw = valueNode.textContent.trim();
          // Animate only clean non-negative integers (optionally grouped).
          const isPlainInt = /^\d{1,3}(?:[. \s]\d{3})*$|^\d+$/.test(raw);
          if (!isPlainInt) return null;
          const target = parseInt(raw.replace(/[^\d]/g, ''), 10);
          if (!Number.isFinite(target) || target <= 0) return null;
          valueNode.textContent = '0';
          return { node: valueNode, target, final: raw };
        });
      }

      run() {
        // Reveal cells with a small stagger.
        this.cells.forEach((cell, i) => {
          setTimeout(() => cell.classList.add('is-revealed'), i * 70);
        });

        const duration = 1100;
        const start = performance.now();
        const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic

        const tick = (now) => {
          const p = Math.min(1, (now - start) / duration);
          const e = ease(p);
          this.targets.forEach((entry) => {
            if (!entry) return;
            if (p >= 1) {
              entry.node.textContent = entry.final; // restore authored grouping
            } else {
              entry.node.textContent = Math.round(entry.target * e).toString();
            }
          });
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }

      // The number element holds a leading text node (the value) followed by the
      // <span.stat-band__unit>. We only touch that first text node.
      firstTextNode(el) {
        for (const node of el.childNodes) {
          if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '') return node;
        }
        return null;
      }
    }
  );
}
